import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtUsd, fmtTimeAgo, STATUS_COLORS } from "@/lib/nexus";
import Sparkline from "@/components/Sparkline";

const FRAMEWORKS = ["OpenAI", "Anthropic", "LangChain", "CrewAI", "AutoGen", "ElizaOS", "LlamaIndex", "Gemini"];
const CHAINS = ["Ethereum", "Base", "Solana", "Polygon", "Arbitrum", "Optimism"];
const STATUSES = ["normal", "watch", "critical"];

const Chip = ({ label, active, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border transition ${
      active ? "border-nexus-cyan text-nexus-cyan bg-nexus-cyan/5" : "border-nexus-border text-nexus-text-2 hover:text-nexus-text hover:border-nexus-border-2"
    }`}
  >{label}</button>
);

export default function Wallets() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ framework: "", chain: "", status: "", q: "" });

  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    params.append("limit", "120");
    api.get(`/agents?${params}`).then((r) => {
      setItems(r.data.items);
      setTotal(r.data.total);
    });
  }, [filters]);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="panel-title">AGENT WALLET INVENTORY</div>
          <h1 className="font-rajdhani font-bold text-2xl tracking-tight mt-1">
            {total} <span className="text-nexus-cyan">wallets</span> across the agentic economy
          </h1>
        </div>
        <input
          data-testid="wallets-search-input"
          placeholder="filter by name or address…"
          value={filters.q}
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          className="bg-nexus-bg border border-nexus-border px-3 py-1.5 text-xs font-mono uppercase tracking-widest w-64 outline-none focus:border-nexus-cyan/60"
        />
      </div>

      <div className="panel p-3 space-y-2">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="panel-title mr-1">Framework</span>
          <Chip label="ALL" active={!filters.framework} onClick={() => setFilters({ ...filters, framework: "" })} testid="filter-framework-all" />
          {FRAMEWORKS.map((f) => (
            <Chip key={f} label={f} active={filters.framework === f} onClick={() => setFilters({ ...filters, framework: f })} testid={`filter-framework-${f}`} />
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="panel-title mr-1">Chain</span>
          <Chip label="ALL" active={!filters.chain} onClick={() => setFilters({ ...filters, chain: "" })} testid="filter-chain-all" />
          {CHAINS.map((c) => (
            <Chip key={c} label={c} active={filters.chain === c} onClick={() => setFilters({ ...filters, chain: c })} testid={`filter-chain-${c}`} />
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="panel-title mr-1">Status</span>
          <Chip label="ALL" active={!filters.status} onClick={() => setFilters({ ...filters, status: "" })} testid="filter-status-all" />
          {STATUSES.map((s) => (
            <Chip key={s} label={s} active={filters.status === s} onClick={() => setFilters({ ...filters, status: s })} testid={`filter-status-${s}`} />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.map((a) => (
          <Link
            key={a.id}
            to={`/agent/${a.id}`}
            data-testid={`wallet-card-${a.id}`}
            className="panel p-3 hover:bg-nexus-panel-2/60 transition brackets group"
          >
            <span className="b1" /><span className="b2" />
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[a.status], boxShadow: `0 0 8px ${STATUS_COLORS[a.status]}` }} />
                  <div className="font-rajdhani font-semibold text-base tracking-wider uppercase text-nexus-text group-hover:text-nexus-cyan">{a.name}</div>
                </div>
                <div className="hash mt-1">{a.short_address}</div>
              </div>
              <div className="text-right">
                <div className="metric-hero text-lg">{fmtUsd(a.balance_usd)}</div>
                <div className="text-[10px] font-mono text-nexus-text-3">balance</div>
              </div>
            </div>
            <div className="mt-3 flex justify-between items-end">
              <div className="text-[10px] font-mono text-nexus-text-2 space-y-0.5">
                <div>fw: <span className="text-nexus-violet">{a.framework}</span></div>
                <div>chain: <span className="text-nexus-cyan">{a.chain}</span></div>
                <div>tx: <span className="text-nexus-text">{a.tx_count.toLocaleString()}</span> · last {fmtTimeAgo(a.last_active)}</div>
              </div>
              <div className="text-right">
                <Sparkline data={a.sparkline} color={STATUS_COLORS[a.status]} />
                <div className="text-[10px] font-mono mt-0.5" style={{ color: STATUS_COLORS[a.status] }}>risk {a.risk_score}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
